/**
 * Selector de cliente para el flujo de cobro.
 *
 * - Búsqueda inline desde 2 caracteres por DNI/RUC o nombre.
 * - Si no hay matches, ofrece crear cliente nuevo (delega al padre via onCreateNew).
 * - Componente controlado: el padre mantiene el `value` (CustomerOut | null).
 */

import { Search, UserPlus, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useSearchCustomers } from "@/queries/customers.ts";
import { CustomerDocType, type CustomerOut } from "@/types/api.ts";

const DOC_TYPE_LABELS: Record<string, string> = {
  [CustomerDocType.DNI]: "DNI",
  [CustomerDocType.RUC]: "RUC",
  [CustomerDocType.FOREIGN]: "Otro",
};

interface Props {
  value: CustomerOut | null;
  onChange: (c: CustomerOut | null) => void;
  /** Si se setea, se highlightea visualmente cuando el cliente elegido no
   *  cumple (ej. RUC requerido y se eligió DNI). */
  requiredDocType?: CustomerDocType;
  /** Callback con el query actual (útil para prefill del form de creación
   *  si lo que tipeó parece un doc_number). */
  onCreateNew?: (currentQuery: string) => void;
}

export function CustomerSelector({ value, onChange, requiredDocType, onCreateNew }: Props) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const search = useSearchCustomers(q);
  const matches = (search.data ?? []).filter((c) => c.is_active);

  // Cerrar dropdown al click fuera.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handlePick = (c: CustomerOut) => {
    onChange(c);
    setQ("");
    setOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setQ("");
  };

  const handleCreate = () => {
    setOpen(false);
    onCreateNew?.(q);
  };

  // Vista cliente seleccionado.
  if (value) {
    const docMismatch = requiredDocType && value.doc_type !== requiredDocType;
    return (
      <div
        className={
          "flex items-center gap-3 rounded-lg border px-3 py-2.5 " +
          (docMismatch ? "border-amber-300 bg-amber-50" : "border-slate-300 bg-white")
        }
      >
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-slate-900">{value.full_name}</p>
          <p className="font-mono text-xs text-slate-500">
            {DOC_TYPE_LABELS[value.doc_type] ?? value.doc_type} {value.doc_number}
          </p>
          {docMismatch && (
            <p className="mt-0.5 text-xs text-amber-800">
              Se requiere {DOC_TYPE_LABELS[requiredDocType!]}.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={handleClear}
          aria-label="Quitar cliente"
          className="rounded-lg p-1.5 text-slate-400 hover:bg-brand-50 hover:text-brand-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  // Vista búsqueda.
  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          type="search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Buscar cliente por DNI/RUC o nombre"
          className="w-full rounded-lg border border-slate-300 py-2.5 pr-3 pl-9 text-sm outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/15"
        />
      </div>

      {open && (q.trim().length >= 2 || matches.length > 0) && (
        <div className="absolute top-full right-0 left-0 z-10 mt-1 max-h-72 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          {search.isLoading && <p className="p-3 text-xs text-slate-500">Buscando…</p>}
          {!search.isLoading && matches.length === 0 && q.trim().length >= 2 && (
            <p className="p-3 text-xs text-slate-500">Sin resultados.</p>
          )}
          {matches.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => handlePick(c)}
              className="flex w-full items-center justify-between gap-3 border-b border-slate-100 px-3 py-2 text-left last:border-b-0 hover:bg-slate-50"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-900">{c.full_name}</p>
                <p className="font-mono text-xs text-slate-500">
                  {DOC_TYPE_LABELS[c.doc_type] ?? c.doc_type} {c.doc_number}
                </p>
              </div>
              <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                {DOC_TYPE_LABELS[c.doc_type] ?? c.doc_type}
              </span>
            </button>
          ))}
          {onCreateNew && (
            <button
              type="button"
              onClick={handleCreate}
              className="flex w-full items-center gap-2 border-t border-slate-200 bg-slate-50 px-3 py-2 text-left text-sm font-medium text-slate-700 hover:bg-slate-100"
            >
              <UserPlus className="h-4 w-4" /> Nuevo cliente
              {q.trim() && <span className="text-xs text-slate-500">"{q.trim()}"</span>}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
